import { ReactNode, useEffect, useState } from "react";

export const fieldClass =
  "mt-1.5 block w-full rounded-lg border-gray-300 bg-white text-sm text-gray-900 focus:border-orange-400 focus:ring-orange-400";

export const formatLabel = (value?: string | null) =>
  value
    ? value
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase())
    : "—";

export const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const errorMessage = (error: unknown) => {
  const value = error as any;
  if (typeof value?.message === "string") return value.message;
  if (typeof value?.error === "string") return value.error;
  const errors = value?.error ?? value?.data?.error;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (typeof first === "string") return first;
  }
  return "The request could not be completed.";
};

export function Button({
  children,
  variant = "primary",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "quiet";
}) {
  const variants = {
    primary: "bg-gray-950 text-white hover:bg-gray-800",
    secondary: "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
    danger: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    quiet: "text-gray-600 hover:bg-gray-100",
  };
  return (
    <button
      {...props}
      className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${props.className ?? ""}`}
    >
      {children}
    </button>
  );
}

export function Card({
  title,
  description,
  action,
  children,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {(title || action) && (
        <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {title && <h2 className="font-semibold text-gray-950">{title}</h2>}
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === "active" || status === "executed" || status === "accepted" || status === "allow"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : status === "deactivated" || status === "rejected" || status === "revoked" || status === "deny"
        ? "border-red-200 bg-red-50 text-red-700"
        : status === "archived"
          ? "border-gray-200 bg-gray-100 text-gray-600"
          : status === "failed"
          ? "border-orange-200 bg-orange-50 text-orange-700"
          : "border-blue-200 bg-blue-50 text-blue-700";
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone}`}>
      {formatLabel(status)}
    </span>
  );
}

export function LoadingRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="animate-pulse divide-y divide-gray-100">
      {Array.from({ length: rows }).map((_, index) => (
        <div className="flex gap-4 px-5 py-5" key={index}>
          <div className="h-10 w-10 rounded-full bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-gray-100" />
            <div className="h-3 w-2/3 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-6 py-14 text-center">
      <i className="fa fa-inbox text-3xl text-gray-300" />
      <h3 className="mt-4 text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{body}</p>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div className="px-6 py-14 text-center">
      <i className="fa fa-triangle-exclamation text-3xl text-red-500" />
      <h3 className="mt-4 text-sm font-semibold text-gray-900">Unable to load data</h3>
      <p className="mt-1 text-sm text-gray-500">{errorMessage(error)}</p>
      <Button className="mt-4" onClick={retry} variant="secondary">Try again</Button>
    </div>
  );
}

export function Drawer({
  open,
  title,
  description,
  close,
  children,
  wide = false,
}: {
  open: boolean;
  title: string;
  description?: string;
  close: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && close();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-gray-950/50" onClick={close} aria-label="Close panel" />
      <div className={`relative flex h-full w-full flex-col bg-white shadow-2xl ${wide ? "max-w-4xl" : "max-w-xl"}`}>
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-950">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          <button className="h-9 w-9 rounded-lg text-gray-500 hover:bg-gray-100" onClick={close} aria-label="Close" type="button">
            <i className="fa fa-xmark" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

export function ReasonDialog({
  open,
  title,
  description,
  confirmLabel,
  busy,
  close,
  confirm,
  danger = false,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  busy: boolean;
  close: () => void;
  confirm: (reason: string) => void;
  danger?: boolean;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-gray-950/50" onClick={close} aria-label="Close dialog" />
      <form
        className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
        onSubmit={(event) => {
          event.preventDefault();
          if (reason.trim()) confirm(reason.trim());
        }}
      >
        <h2 className="text-lg font-semibold text-gray-950">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-gray-500">{description}</p>
        <label className="mt-5 block text-sm font-medium text-gray-700">
          Reason <span className="text-orange-600">*</span>
          <textarea
            autoFocus
            className={`${fieldClass} min-h-[100px] resize-y`}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Record why this action is necessary"
            value={reason}
          />
        </label>
        <div className="mt-6 flex justify-end gap-3">
          <Button onClick={close} type="button" variant="secondary">Cancel</Button>
          <Button disabled={busy || !reason.trim()} type="submit" variant={danger ? "danger" : "primary"}>
            {busy ? <i className="fa fa-circle-notch fa-spin" /> : null}
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function Pager({
  page,
  totalPages,
  setPage,
}: {
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
      <span className="text-sm text-gray-500">Page {page} of {Math.max(totalPages, 1)}</span>
      <div className="flex gap-2">
        <Button disabled={page <= 1} onClick={() => setPage(page - 1)} variant="secondary">Previous</Button>
        <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)} variant="secondary">Next</Button>
      </div>
    </div>
  );
}
